

import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {
  currentUserModule,
  dataFieldsEditModule, queryModule,
  studyDefinitionModule,
  studyModule,
  studyPatientContext,
  studyPatientModule,
  visitModule
} from "@/store";
import DataFields from "@/components/study/DataFields.vue";
import {convertToUpdateDtos, findRedirectRuleFields} from "@/auxillary/datafield.cache.helpers";
import {translate, translateFilter} from "@/auxillary/translation";
import {MdbsCore} from "@/api/tsclient";
import Rights = MdbsCore.Rights;
import DataFieldCache from "@/models/data.field.cache";
import InstrumentContextCache from "@/models/instrument.context.cache";
import VisitContextCache from "@/models/visit.context.cache";
import {Dictionary} from "vue-router/types/router";
import YesNoCancelDialog from "@/components/common/YesNoCancelDialog.vue";
import {canSeeVisitQueries} from "@/components/query/QueryPermissions";
import DataFieldsChangedDialog from "@/components/study/dataFieldChange/DataFieldsChangedDialog.vue";
import {fieldsWithValuesWillBeChanged, IDataFieldChangeReport} from "@/auxillary/datafield.changes";

@Component({
  components: {
    DataFieldsChangedDialog,
    DataFields,
    YesNoCancelDialog
  },
  filters: {
    translateFilter
  }
})
export default class VisitEditView extends Vue {
  // Todo: not working properly
  @Prop({default: ""}) private visitId: string;

  currentVisitContext: Readonly<VisitContextCache> | null = null;
  private saving: boolean = false;
  private snackbarText = "";
  private showSnackbar = false;
  private snackbarColor = "success";

  showRedirectDialog = false;
  redirectDialogText = "";
  redirectDialogSubject = "";
  currentRedirectConfig: any = {};

  showChangeDialog = false;
  changedFields: Array<IDataFieldChangeReport> = [];

  @Watch('currentRedirectConfig', {immediate: true, deep: true})
  private onRedirectConfigChanged() {
    if (this.currentRedirectConfig?.field === undefined) {
      this.redirectDialogText = "";
      return;
    }

    this.redirectDialogSubject = translate(this.currentRedirectConfig.field.redirectRule?.definition?.subjectDisplayKey);
    this.redirectDialogText = translate(this.currentRedirectConfig.field.redirectRule?.definition?.textDisplayKey);
  }

  get loading() {
    return visitModule.isLoading ||
        studyPatientModule.isLoading ||
        studyModule.isLoading ||
        studyDefinitionModule.isLoading ||
        dataFieldsEditModule.isLoading;
  }

  get canSave() {
    return this.isValid && this.isEditable;
  }

  get isValid() {
    // TODO Demo Hack
    // basic check for required field
    return dataFieldsEditModule.dataFields.every(
        d => !d.isVisible || d.values.value != null
    )
  }

  get isEditable() {
    return this.canEditVisit && (this.currentVisitContext?.isEditable ?? false);
  }

  get showNotEditableReason() {
    return (this.currentVisitContext?.isEditable === false) ?? false;
  }

  get notEditableReason() {
    return this.currentVisitContext?.notEditableReason ?? "";
  }

  get canEditVisit() {
    return currentUserModule.hasRight(Rights.VisitUpdateCmd)
  }

  get patient() {
    return studyPatientModule.studyPatient;
  }

  get visit() {
    return visitModule.visit;
  }

  async mounted() {
    await visitModule.loadById(this.visitId)
    await studyPatientModule.loadById(visitModule.visit.patientId ?? "")
    this.currentVisitContext =  studyPatientContext.visitById(this.visitId)  ?? null;
    await studyModule.loadById(studyPatientModule.studyPatient.studyId ?? "")
    await studyDefinitionModule.loadById(studyModule.study?.studyDefinitionId ?? "")
    const visitDefinition = studyDefinitionModule.visitDefinitionById(visitModule.visit.definitionId ?? "");
    dataFieldsEditModule.setEditable(this.isEditable)
    await dataFieldsEditModule.loadDataFields({
      definitions: visitDefinition?.fields ?? [],
      dataFields: visitModule.visit?.fields ?? [],
      validationMessages: visitModule.visit?.validationMessages
    })
    if (this.visitId != undefined && canSeeVisitQueries())
      await queryModule.loadQueriesForFields({referenceType: "VisitDefinition", referenceId: this.visitId});
  }

  async checkForFieldChangeBeforeSave() {
    const changedFields = fieldsWithValuesWillBeChanged( dataFieldsEditModule.dataFields);
    if (changedFields.length > 0){
      this.showChangeDialog = true;
      this.changedFields = changedFields;
    } else {
      await this.saveAfterChangeCheck();
    }
  }

  async saveAfterChangeCheck() {
    this.showChangeDialog = false;
    await this.save();
  }


  async cancel() {
    await this.$router.back();
  }

  async save() {

    this.saving = true

    const fields = convertToUpdateDtos(dataFieldsEditModule.dataFields)

    await visitModule.update({
      id: this.visit.id,
      displayKey: this.visit.displayKey,
      order: this.visit.order,
      patientId: this.visit.patientId,
      lockedManually: this.visit.lockedManually,
      identifier: this.visit.identifier,
      fields
    })

    dataFieldsEditModule.setValidationMessages(visitModule.visit?.validationMessages ?? []);

    if (visitModule.visit.canSave){
      this.showUpdateSucceededSnackBar()
      this.checkShowRedirectDialog();

      if (!this.showRedirectDialog)
        await this.goBack();
    }

    this.showUpdateFailedSnackBar();
    this.saving = false
  }

  async goBack() {
    await this.$router.back();
  }

  showUpdateSucceededSnackBar() {
    this.snackbarColor = "success";
    this.snackbarText = "Alle Änderungen wurden gespeichert."
    this.showSnackbar = true;
  }

  showUpdateFailedSnackBar() {
    if (visitModule.visit?.validationMessages?.some(v => v.messageType == "error"))
      this.snackbarColor = "error";
    else
      this.snackbarColor = "warning";
    this.snackbarText = "Invalide Daten, Änderungen konnten nicht gespeichert werden."
    this.showSnackbar = true;
  }

  private checkShowRedirectDialog() {
    this.currentRedirectConfig = {};

    const redirectFields = findRedirectRuleFields(dataFieldsEditModule.dataFields);
    if (redirectFields.length == 0) return;

    const sortedRedirectFields = redirectFields.sort(function( field1: DataFieldCache, field2: DataFieldCache ){
      return (field2.redirectRule?.definition?.priority ?? 0) - (field1.redirectRule?.definition?.priority ?? 0);
    });

    for (let field of sortedRedirectFields) {
      const targetVisitIdentifier = field.redirectRule?.definition?.targetVisitIdentifier;
      const targetInstrumentIdentifier = field.redirectRule?.definition?.targetInstrumentIdentifier;

      const visitContextCache = targetVisitIdentifier
          ? studyPatientContext.visits.find(v => v.identifier == targetVisitIdentifier)
          : undefined;

      if (visitContextCache) {
        if (visitContextCache.isNew) continue;

        const instrumentContextCache = visitContextCache.visibleInstruments.find(i => i.identifier == targetInstrumentIdentifier);
        if (!instrumentContextCache) continue;

        if (!field.isRedirectVisible) continue;

        this.setCurrentRedirectConfig(field, false, instrumentContextCache, visitContextCache);
        this.showRedirectDialog = true;
        return;
      }

      const instrumentContextCache = studyPatientContext.standaloneInstruments.find(i => i.identifier == targetInstrumentIdentifier);
      if (!instrumentContextCache) continue;

      if (!field.isRedirectVisible) continue;

      this.setCurrentRedirectConfig(field, true, instrumentContextCache, undefined);
      this.showRedirectDialog = true;
      return;
    }
  }

  private setCurrentRedirectConfig(
      field: DataFieldCache,
      isStandaloneInstrument: boolean,
      instrumentContextCache: Readonly<InstrumentContextCache>,
      visitContextCache: Readonly<VisitContextCache> | undefined) {
    this.currentRedirectConfig = {field, isStandaloneInstrument, instrumentContextCache, visitContextCache}
  }

  private async applyRedirectRule() {
    this.showRedirectDialog = false;

    if (!this.currentRedirectConfig.field) {
      return;
    }

    const {isStandaloneInstrument, instrumentContextCache, visitContextCache} = this.currentRedirectConfig;
    if (isStandaloneInstrument) {
      await this.openStandaloneInstrument(instrumentContextCache);
      return;
    }

    await this.openVisitInstrument(visitContextCache, instrumentContextCache);
    return;
  }

  async openStandaloneInstrument(instrumentContext: InstrumentContextCache) {
    if (instrumentContext.isNew) {
      await this.navigate("standaloneinstrumentnew", {
        patientId: this.patient.id,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    } else {
      await this.navigate("standaloneinstrumentedit", {
        patientId: this.patient.id,
        instrumentId: instrumentContext.id
      });
    }
  }

  async openVisitInstrument(visitContext: VisitContextCache, instrumentContext: InstrumentContextCache) {
    if (instrumentContext.isNew) {
      await this.navigate("visitinstrumentnew", {
        patientId: this.patient.id,
        visitId: visitContext.id,
        instrumentDefinitionId: instrumentContext.definitionId
      });
    } else {
      await this.navigate("visitinstrumentedit", {
        patientId: this.patient.id,
        visitId: visitContext.id,
        instrumentId: instrumentContext.id
      });
    }
  }

  async navigate(name: string, params: Dictionary<string>) {
    return await this.$router.push({name, params})
        .catch(() => {
          return undefined;
        });
  }
}
